/**
 * Public Trial service.
 *
 * Backs the /subscribe and /checkout pages used to share self-serve
 * subscription links while the public "Assinar" CTA stays hidden on the
 * landing page.
 *
 * The token (the `code` query param) is a Django-signed payload created via
 * the `issue_public_trial_link` management command. The same token grants
 * access to:
 *   - POST /api/landing/public-trial/register/ (account creation)
 *   - POST /api/billing/create-checkout-session/ (Stripe checkout)
 */

import axios from 'axios';
import apiClient, { _clearPersistentCache, _clearSharedGetCache } from './api';
import { jwtDecode } from 'jwt-decode';

const LOCAL_BACKEND = 'http://127.0.0.1:8000/api';

const sharedRequestOptions = {
  __skipAuthHeader: true,
  __skipAuthRefresh: true,
  __skipLocalRedirect: true,
};

async function withLocalFallback(method, url, body, opts = {}) {
  // Mirrors the auth/billing services: if the production backend rejects an
  // unauthenticated public-trial call (404/400/500), retry the local backend
  // so developers can iterate locally without redeploying.
  try {
    const response =
      method === 'get'
        ? await apiClient.get(url, opts)
        : await apiClient.post(url, body, opts);
    return response.data;
  } catch (err) {
    const transientStatuses = new Set([400, 404, 500]);
    if (!transientStatuses.has(err?.response?.status)) {
      throw err;
    }
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (method === 'get') {
        const params = opts?.params
          ? '?' + new URLSearchParams(opts.params).toString()
          : '';
        const { data } = await axios.get(`${LOCAL_BACKEND}${url}${params}`, {
          headers,
          timeout: opts?.timeout || 20000,
        });
        return data;
      }
      const { data } = await axios.post(`${LOCAL_BACKEND}${url}`, body, {
        headers,
        timeout: opts?.timeout || 20000,
      });
      return data;
    } catch {
      throw err;
    }
  }
}

export const publicTrialService = {
  async validate(code) {
    const cleanCode = String(code || '').trim();
    if (!cleanCode) {
      return { valid: false, error: 'missing_code' };
    }
    return withLocalFallback('get', '/landing/public-trial/validate/', null, {
      ...sharedRequestOptions,
      params: { code: cleanCode },
      timeout: 15000,
    });
  },

  async register(payload = {}) {
    const data = await withLocalFallback(
      'post',
      '/landing/public-trial/register/',
      payload,
      { ...sharedRequestOptions, timeout: 30000 }
    );

    // Mirror authService.login: persist the freshly issued tokens so the
    // user is treated as logged-in by the rest of the app.
    const access = data?.tokens?.access;
    const refresh = data?.tokens?.refresh;
    if (access && refresh) {
      try {
        // Public trial registration is an identity switch from a shared link.
        // Clear any previous Aviatto/tenant browser state here too, not only
        // in the page component, so stale profile/cache data cannot survive if
        // this service is reused elsewhere.
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('user_profile');
        localStorage.removeItem('aviatto_local_session');
        _clearSharedGetCache();
        _clearPersistentCache();

        localStorage.setItem('access_token', access);
        localStorage.setItem('refresh_token', refresh);
        let decoded = null;
        try {
          decoded = jwtDecode(access);
          if (decoded) {
            localStorage.setItem('user', JSON.stringify(decoded));
          }
        } catch {
          /* token shape is opaque to us — non-fatal */
        }
        if (data?.user) {
          const tenantSchema = (
            data.user.tenant_schema
            || decoded?.tenant_schema
            || data?.tenant?.schema_name
            || ''
          );
          localStorage.setItem(
            'user_profile',
            JSON.stringify({
              ...data.user,
              ...(tenantSchema ? { tenant_schema: tenantSchema } : {}),
            })
          );
        }
        if (typeof apiClient.rehydrateSyncCache === 'function') {
          apiClient.rehydrateSyncCache();
        }
      } catch {
        /* localStorage may be unavailable in private mode — non-fatal */
      }
    }

    return data;
  },
};

export default publicTrialService;
